import { useMemo } from 'react';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { setCheckedFunctions, useCheckedFunctions } from 'reducers/viewsSlice';

import { VIEWS_FILTER_KEYS } from 'constants/ProjectConstants';

import { ListItemComp } from 'uiToolkitV1/ListItemComp';

import { useFunctions } from '../../Hooks/dataHooks';
import { AgGridContainer } from './AgGridContainer';

const CellRenderer = ({ data }) => {
  const { name, subFunctionCount } = data;

  return (
    <ListItemComp
      withoutBackground
      padding="16px 24px"
      title={name}
      isBoldText={false}
      subTitle={subFunctionCount !== 0 ? `${subFunctionCount} Sub Functions` : ''}
    />
  );
};

export const Functions = () => {
  const { data: functions, isLoading } = useFunctions();
  const checkedFunctions = useCheckedFunctions();

  const checkedRowsSet = useMemo(() => new Set(checkedFunctions?.map((c) => c.id) ?? []), [checkedFunctions]);

  const columnDefs = useMemo(
    () => [
      {
        colId: VIEWS_FILTER_KEYS.FUNCTION,
        getQuickFilterText: (params) => params.data.name,
        comparator: (_valueA, _valueB, nodeA, nodeB, isDescending) => {
          const a = nodeA.data.name;
          const b = nodeB.data.name;
          // these checks are for moving checked rows to the top
          if (checkedRowsSet.has(nodeA.data.uuid) && checkedRowsSet.has(nodeB.data.uuid)) {
            return a.localeCompare(b);
          }
          if (checkedRowsSet.has(nodeA.data.uuid)) {
            return isDescending ? 1 : -1;
          }
          if (checkedRowsSet.has(nodeB.data.uuid)) {
            return isDescending ? -1 : 1;
          }
          return a.localeCompare(b);
        },
      },
    ],
    [checkedRowsSet]
  );

  const autoGroupColumnDef = useMemo(
    () => ({
      width: 300,
      sortable: true,
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: CellRenderer,
      },
    }),
    []
  );

  const getDataPath = useMemo(() => (data) => data.hierarchy, []);

  return (
    <AgGridContainer
      rowData={functions}
      treeData
      getDataPath={getDataPath}
      setCheckedRows={setCheckedFunctions}
      columnDefs={columnDefs}
      autoGroupColumnDef={autoGroupColumnDef}
      onRowClicked={(e) => {
        const { node } = e;
        node.setExpanded(!node.expanded);
      }}
      sortColumnId={VIEWS_FILTER_KEYS.FUNCTION}
      checkedRows={checkedFunctions}
      rowCheckedTesterFn={({ nodeData, checkedRows }) => {
        const { uuid } = nodeData || {};
        const checked = checkedRows?.find(({ id }) => id === uuid);
        return !!checked;
      }}
    />
  );
};
